import type { ComponentType } from 'react';
import {
  AmenitiesEnum,
  CourtSurfacesEnum,
  VenueAccessTypesEnum,
  VenueFacilityTypesEnum,
  VenueNetsEnum,
} from 'types/generated/server';
import AccessibleParking from 'svg/AccessibleParking';
import AirConditioning from 'svg/AirConditioning';
import Bar from 'svg/Bar';
import Cafe from 'svg/Cafe';
import ChildFriendly from 'svg/ChildFriendly';
import CourtFlat from 'svg/CourtFlat';
import DogFriendly from 'svg/DogFriendly';
import LightBulb from 'svg/LightBulb';
import Outdoor from 'svg/Outdoor';
import Parking from 'svg/Parking';
import Racket from 'svg/Racket';
import Restaurant from 'svg/Restaurant';
import Restroom from 'svg/Restroom';
import Shop from 'svg/Shop';
import Shower from 'svg/Shower';
import Water from 'svg/Water';
import Wheelchair from 'svg/Wheelchair';
import Wifi from 'svg/Wifi';

type IconComponent = ComponentType<{ className?: string }>;

/** How many amenities to show before the "Show all" toggle reveals the rest. */
export const AMENITIES_PREVIEW_COUNT = 6;

/** Character length past which the About section collapses behind "Show more". */
export const ABOUT_PREVIEW_LENGTH = 260;

/**
 * Icon for each amenity (labels come from the shared AMENITY_LABELS). Complete
 * map — every enum value maps to an icon, matching the Figma amenity list.
 */
export const AMENITY_CONFIG: Record<AmenitiesEnum, IconComponent> = {
  [AmenitiesEnum.Lights]: LightBulb,
  [AmenitiesEnum.AirConditioning]: AirConditioning,
  [AmenitiesEnum.Restrooms]: Restroom,
  [AmenitiesEnum.Shower]: Shower,
  [AmenitiesEnum.FreeWifi]: Wifi,
  [AmenitiesEnum.Water]: Water,
  [AmenitiesEnum.Restaurant]: Restaurant,
  [AmenitiesEnum.Bar]: Bar,
  [AmenitiesEnum.Cafe]: Cafe,
  [AmenitiesEnum.OutdoorSeating]: Outdoor,
  [AmenitiesEnum.ProShop]: Shop,
  [AmenitiesEnum.EquipmentRental]: Racket,
  [AmenitiesEnum.WheelchairAccessible]: Wheelchair,
  [AmenitiesEnum.AccessibleParking]: AccessibleParking,
  [AmenitiesEnum.ChildFriendly]: ChildFriendly,
  [AmenitiesEnum.DogFriendly]: DogFriendly,
  [AmenitiesEnum.Parking]: Parking,
  // Legacy values (not offered in the new form, kept so older data still renders).
  [AmenitiesEnum.Backboard]: CourtFlat,
  [AmenitiesEnum.Food]: Restaurant,
  [AmenitiesEnum.LockerRooms]: Restroom,
  [AmenitiesEnum.Training]: Racket,
};

export const SURFACE_LABELS: Partial<Record<CourtSurfacesEnum, string>> = {
  [CourtSurfacesEnum.Acrylic]: 'Acrylic',
  [CourtSurfacesEnum.Asphalt]: 'Asphalt',
  [CourtSurfacesEnum.Carpet]: 'Carpet',
  [CourtSurfacesEnum.Clay]: 'Clay',
  [CourtSurfacesEnum.Concrete]: 'Concrete',
  [CourtSurfacesEnum.Grass]: 'Grass',
  [CourtSurfacesEnum.Hard]: 'Hard',
  [CourtSurfacesEnum.Wood]: 'Wood',
};

const FACILITY_LABELS: Partial<Record<VenueFacilityTypesEnum, string>> = {
  [VenueFacilityTypesEnum.Club]: 'Club',
  [VenueFacilityTypesEnum.Private]: 'Private',
  [VenueFacilityTypesEnum.Public]: 'Public',
  [VenueFacilityTypesEnum.School]: 'School',
};

const ACCESS_LABELS: Partial<Record<VenueAccessTypesEnum, string>> = {
  [VenueAccessTypesEnum.Free]: 'Free',
  [VenueAccessTypesEnum.Membership]: 'Membership required',
  [VenueAccessTypesEnum.OneTime]: 'One-time fee',
  [VenueAccessTypesEnum.Private]: 'Private',
};

/** Best-effort equipment-availability copy derived from the pickleball nets setting. */
const NET_LABELS: Partial<Record<VenueNetsEnum, string>> = {
  [VenueNetsEnum.Permanent]: 'Provided',
  [VenueNetsEnum.Portable]: 'Provided',
  [VenueNetsEnum.Tennis]: 'Provided',
  [VenueNetsEnum.BringYourOwn]: 'Bring your own',
};

export const getEquipmentLabel = (nets?: VenueNetsEnum | null): string | null =>
  (nets && NET_LABELS[nets]) || null;

/** Joins facility + access type into the "Private • Membership required" summary line. */
export const getAccessSummary = (
  facilityType?: VenueFacilityTypesEnum | null,
  accessType?: VenueAccessTypesEnum | null,
): string =>
  [
    facilityType ? FACILITY_LABELS[facilityType] : null,
    accessType ? ACCESS_LABELS[accessType] : null,
  ]
    .filter(Boolean)
    .join(' • ');
